import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

interface DarbInvoiceTemplateProps {
  invoice: any;
  lineItems: any[];
  billTo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    vatNumber?: string;
    policyNumber?: string;
    vinNumber?: string;
    insuranceCompany?: string;
  };
  invoiceType?: 'customer' | 'insurance';
  relatedRequest?: any;
}

const DarbInvoiceTemplate: React.FC<DarbInvoiceTemplateProps> = ({ invoice, lineItems, billTo, invoiceType: propInvoiceType, relatedRequest }) => {
  const data = invoice?.attributes || invoice;
  const invoiceType = propInvoiceType || (['insurance', 'insurance_company_monthly', 'insurance_customer'].includes(data.invoiceType) ? 'insurance' : 'customer');
  
  // Company information
  const companyInfo = {
    name: 'DARB NATIONAL SERVICE LLC',
    cr: '1366462',
    address: 'BLOCK 255, STREET, WAY 5508, BUILDING 564',
    vatNum: 'OM1100429932',
    phone: '24790888 - 80081818',
    email: 'INFO@DARBNS.COM',
  };

  const formatCurrency = (amount: number) => {
    if (!amount && amount !== 0) return 'ر.ع. 0.00';
    return `ر.ع. ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Use stored invoice totals (do not recalculate)
  const subtotal = data.subtotal ?? 0;
  const discount = data.discount ?? 0;
  const taxRate = data.taxRate ?? 0.05;
  const salesTax = data.tax ?? 0;
  const other = data.other ?? 0;
  const total = data.total ?? 0;
  const taxRatePercent = taxRate > 1 ? taxRate : taxRate * 100;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '210mm',
        margin: '0 auto',
        padding: '20mm',
        backgroundColor: '#fff',
        fontFamily: 'Arial, sans-serif',
        '@media print': {
          padding: '20mm',
          margin: 0,
        },
      }}
    >
      {/* Header with Logo */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            width: '150px',
            height: '60px',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <img
            src="/cropped-pixelcut-export-removebg-2048x691.png"
            alt="DARB Logo"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </Box>
        
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            fontSize: '20px',
            mb: 2,
            color: '#000',
            textTransform: 'uppercase',
          }}
        >
          {companyInfo.name}. TAX INVOICE
        </Typography>
      </Box>

      {/* Company Info and Invoice Details Row */}
      <Box sx={{ mb: 3, fontSize: '12px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '12px' }}>
            CR:{companyInfo.cr} BLOCK 255 , STREET ,WAY 5508 , BUILDING 564
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
            DATE: {formatDate(data.invoiceDate)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '12px' }}>
            VAT NUM : {companyInfo.vatNum}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
            INVOICE # {data.invoiceNumber || '-'}
          </Typography>
        </Box>
        {data.omanTaxId && (
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
            Oman Tax ID: {data.omanTaxId}
          </Typography>
        )}
        <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
          {companyInfo.phone}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '12px' }}>
          {companyInfo.email}
        </Typography>
      </Box>

      {/* BILL TO Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: '12px',
            fontWeight: 'bold',
            mb: 1,
            color: '#000',
            textTransform: 'uppercase',
          }}
        >
          BILL TO : {billTo?.name || '-'}
        </Typography>
        {invoiceType === 'insurance' && billTo?.insuranceCompany && (
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 1, fontWeight: 'bold' }}>
            Insurance Company: {billTo.insuranceCompany}
          </Typography>
        )}
        {invoiceType === 'insurance' && billTo?.policyNumber && (
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
            Policy Number: {billTo.policyNumber}
          </Typography>
        )}
        {invoiceType === 'insurance' && billTo?.vinNumber && (
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
            VIN Number: {billTo.vinNumber}
          </Typography>
        )}
        {billTo?.vatNumber && (
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
            VAT R C: {billTo.vatNumber}
          </Typography>
        )}
        {billTo?.address && (
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
            {billTo.address}
          </Typography>
        )}
        {billTo?.phone && (
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
            {billTo.phone}
          </Typography>
        )}
        {billTo?.email && (
          <Typography variant="body2" sx={{ fontSize: '12px' }}>
            {billTo.email}
          </Typography>
        )}
      </Box>

      {/* Line Items Table */}
      <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 'none', border: '1px solid #ddd' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '12px',
                  borderBottom: '2px solid #000',
                  textAlign: 'left',
                  py: 1,
                }}
              >
                DESCRIPTION
              </TableCell>
              {invoiceType === 'insurance' && (
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '12px',
                    borderBottom: '2px solid #000',
                    textAlign: 'left',
                    py: 1,
                  }}
                >
                  SERVICE
                </TableCell>
              )}
              {invoiceType === 'insurance' && (
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '12px',
                    borderBottom: '2px solid #000',
                    textAlign: 'right',
                    py: 1,
                  }}
                >
                  RATE
                </TableCell>
              )}
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '12px',
                  borderBottom: '2px solid #000',
                  textAlign: 'right',
                  py: 1,
                }}
              >
                AMOUNT
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems && lineItems.length > 0 ? (
              lineItems.map((item: any, index: number) => {
                const attrs = item.attributes || item;
                const product = attrs?.product?.data?.attributes || attrs?.product;
                let description = attrs?.description || product?.name || `Item ${index + 1}`;
                const amount = attrs?.total || attrs?.amount || 0;
                
                // For insurance invoices, extract service name and rate from description or related request
                let serviceName = '';
                let serviceRate = '';
                if (invoiceType === 'insurance' && relatedRequest) {
                  const requestAttrs = relatedRequest.attributes || relatedRequest;
                  const serviceCode = requestAttrs.serviceCode;
                  if (serviceCode) {
                    // Map service codes to names
                    const serviceNames: { [key: string]: string } = {
                      'TOWING_SERVICE': 'Vehicle Towing',
                      'BATTERY_BOOST': 'Battery Boost',
                      'LOCKOUT_SERVICE': 'Lockout Assistance',
                      'FUEL_DELIVERY': 'Fuel Delivery',
                    };
                    serviceName = serviceNames[serviceCode] || serviceCode;
                    
                    // Get rate from unitPrice or calculate from description
                    serviceRate = formatCurrency(attrs?.unitPrice || 0);
                  }
                }

                return (
                  <TableRow key={item.id || index}>
                    <TableCell sx={{ fontSize: '12px', borderBottom: '1px solid #ddd', py: 1 }}>
                      {description}
                    </TableCell>
                    {invoiceType === 'insurance' && (
                      <TableCell sx={{ fontSize: '12px', borderBottom: '1px solid #ddd', py: 1 }}>
                        {serviceName || '-'}
                      </TableCell>
                    )}
                    {invoiceType === 'insurance' && (
                      <TableCell sx={{ fontSize: '12px', borderBottom: '1px solid #ddd', textAlign: 'right', py: 1 }}>
                        {serviceRate || '-'}
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        fontSize: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'right',
                        py: 1,
                      }}
                    >
                      {formatCurrency(amount)}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={invoiceType === 'insurance' ? 4 : 2} sx={{ fontSize: '12px', textAlign: 'center', py: 2 }}>
                  No items
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary of Charges */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box sx={{ minWidth: '300px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              SUBTOTAL
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
              {formatCurrency(subtotal)}
            </Typography>
          </Box>
          {discount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                DISCOUNT
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                - {formatCurrency(discount)}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              TAX RATE
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
              {Number(taxRatePercent).toFixed(0)}%
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              SALES TAX
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
              {formatCurrency(salesTax)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              OTHER
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
              {formatCurrency(other)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 2,
              pt: 2,
              borderTop: '2px solid #000',
              backgroundColor: '#f0f0f0',
              padding: '8px',
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              TOTAL
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              {formatCurrency(total)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          mt: 4,
          fontSize: '11px',
          lineHeight: 1.6,
          color: '#000',
        }}
      >
        <Typography variant="body2" sx={{ fontSize: '11px', mb: 1 }}>
          Make all checks payable to DARB NATIONAL SERVICE LLC.
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '11px', mb: 1 }}>
          If you have any questions concerning this invoice contact DARB at 80081818 or {companyInfo.email}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '12px',
            fontWeight: 'bold',
            mt: 2,
            textTransform: 'uppercase',
          }}
        >
          THANK YOU FOR YOUR BUSINESS!
        </Typography>
      </Box>
    </Box>
  );
};

export default DarbInvoiceTemplate;

